import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { usePatientMedicationCreate } from "../../../../../../../queries/medication/use-patient-medication-create";
import { usePatientMedicationUpdate } from "../../../../../../../queries/medication/use-patient-medication-update";
import { usePatientMedicationList } from "../../../../../../../queries/medication/use-patient-medication-list";
import { useProvidersList } from "../../../../../../../queries/provider/setting/use-providers-list";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getValueFromObj,
  getXtenantId,
} from "../../../../../../../utils/utils";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import MaskMultiSelect from "../../../../../../../components/MaskMultiSelect";
import { useExportClinicalToEncounter } from "../../../../../../../queries/encounter/use-export-clinical-to-encounter";
import { ExportClinicalDataToEncounterTypeEnum } from "../../../../../../../services";
import {
  DOSAGE_LIST,
  FREQUENCY_LIST,
  MEDICINE_INITIAL_VALUE,
  MEDICINE_VALIDATION_SCHEMA,
  Medication,
  ROUTE_LIST,
  WHEN_LIST,
} from "./medication-constant";
import dayjs from "dayjs";
import AsyncSelect from "../../../../../../../components/AsyncSelect";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useGetCataLogCode } from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import Text from "../../../../../../../components/Text";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";
import NumberInput from "../../../../../../../components/NumberInput";

interface UpsertMedicationsDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertMedicationsDrawer = ({
  disclosureHost,
}: UpsertMedicationsDrawerProps) => {
  // const encounterPatientId = useSearchParams()[0].get("patientid");
  // const appointmentId = useSearchParams()[0].get("appointmentid") ?? undefined;

  // note : we are using useQueryParams instead of useSearchParams because we cannot access the search params in the drawer
  // const encounterPatientId = useQueryParams("patientid");
  const appointmentId = useQueryParams("appointmentid");

  const { mutateAsync: createMedication } = usePatientMedicationCreate();
  const { mutateAsync: updateMedication } = usePatientMedicationUpdate();
  const { mutateAsync: exportClinical } = useExportClinicalToEncounter();
  const showSnackbar = useSnackbar();
  const { patientId } = useParams();
  // const patientData: any = useOutletContext();
  const queryClient = useQueryClient();
  const prefillData = disclosureHost?.data;

  const [providerSearchKey, setProviderSearchKey] = useDebounce<string>("");
  const [searchKey, setSearchKey] = useDebounce<string>(
    disclosureHost?.data?.medication?.name ?? ""
  );
  const { data: medicinesList } = useGetCataLogCode({
    gridSorting: [],
    pagination: {
      page: 0,
      pageSize: 5,
    },
    searchKey: searchKey ?? undefined,
    type: "DRUG_CATALOG",
    archive: false,
    active: true,
    enabled: disclosureHost.opened,
  });

  const {
    data: providersList,
    isLoading,
    isRefetching,
  } = useProvidersList({
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
    status: true,
    searchKey: providerSearchKey,
    enabled: disclosureHost.opened,
  });

  const transformedMedicines = useMemo(
    () =>
      medicinesList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.drugName + " - " + (it?.description ?? ""),
      })),
    [medicinesList]
  );

  const transformedProviders = useMemo(
    () =>
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
      })),
    [providersList]
  );

  const [me] = useCurrentUser();

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [variant, setVariant] = useState<"import" | "default">("default");
  const [selectedRecords, setSelectedRecords] = useState<any>();

  const { data: medicationListData, isLoading: isMedicationListLoading } =
    usePatientMedicationList({
      patientUuid: patientId ?? "",
      pagination: DEF_PAGINATION,
      gridSorting: [],
      archive: false,
      active: true,
      enabled: variant === "import",
    });

  const medicationOptions = useMemo(() => {
    return medicationListData?.data.content.map((item: any) => ({
      value: item.uuid,
      label: getValueFromObj(item?.medicalCodes) ?? "",
    }));
  }, [medicationListData?.data.content]);

  const formik = useFormik<Medication>({
    initialValues: prefillData || {
      ...MEDICINE_INITIAL_VALUE,
      prescribedBy: {
        value: me?.uuid || "",
        label: `${me?.firstName || ""} ${me?.lastName || ""}`,
      },
    },
    validationSchema: MEDICINE_VALIDATION_SCHEMA,
    onSubmit: handleCreateMedication,
    enableReinitialize: true,
  });

  async function handleCreateMedication(values?: Medication) {
    const selectedPatient = {
      [patientId as string]: "",
    };

    const prescribedBy = values?.prescribedBy
      ? {
          [values.prescribedBy.value]: values.prescribedBy.label,
        }
      : undefined;

    const payload = {
      patientMedication: {
        ...values,
        patient: selectedPatient,
        prescribedBy: prescribedBy as any,
        appointmentId: appointmentId ?? undefined,
        medicalCodes: values?.medicalCodes
          ? {
              [values?.medicalCodes as unknown as string]: "",
            }
          : undefined,
        // sending medication bcox we are sending the medicine name in the medicalCodes
        medication: undefined,
      },
      xTENANTID: getXtenantId(),
    };

    if (prefillData) showToastAndResetForm(updateMedication(payload as any));
    else showToastAndResetForm(createMedication(payload as any));
  }

  async function handleImportMedications() {
    const recordUuids = (selectedRecords ?? [])
      .map((record: any) => record?.value)
      .filter(Boolean);

    if (!recordUuids.length) {
      showSnackbar(getErrorSnackbar("Please select at least one record."));
      return;
    }

    showToastAndResetForm(
      exportClinical({
        type: ExportClinicalDataToEncounterTypeEnum.Medication,
        appointmentUuid: appointmentId ?? "",
        recordUuids,
        xTENANTID: getXtenantId(),
      })
    );
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        queryClient.invalidateQueries({
          queryKey: ["patient-medication-list"],
        });

        showSnackbar(getSuccessSnackbar(res));

        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndReset() {
    formik.resetForm();
    setVariant("default");
    setSelectedRecords(undefined);
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle={prefillData ? "Edit Medication" : "Add Medication"}
      dialogAction={
        <StyledButtonV2
          label={variant === "import" ? "Import" : "Save"}
          variant="contained"
          onClick={
            variant === "import" ? handleImportMedications : formik.submitForm
          }
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "700px",
          height: variant === "import" ? "auto" : "450px",
        },
      }}
    >
      {isVisitNotePage && (
        <Box
          sx={{
            pt: 1,
            px: 1.5,
          }}
        >
          <LabeledSwitch
            label="import from existing records"
            checked={variant === "import"}
            onChange={(e) => {
              setVariant(e.target.checked ? "import" : "default");
            }}
          />
        </Box>
      )}

      {variant === "import" && (
        <Grid
          container
          spacing={3}
          sx={{
            pt: 0,
          }}
        >
          <Grid size={12}>
            <MaskMultiSelect
              required
              loading={isMedicationListLoading}
              label="Medicine Name"
              optionWithCheckbox={true}
              placeholder="Select from Existing Medication Records"
              options={medicationOptions}
              value={selectedRecords}
              onChange={(v) => setSelectedRecords(v)}
            />
          </Grid>
        </Grid>
      )}

      {variant === "default" && (
        <Grid
          container
          spacing={3}
          sx={{
            pt: isVisitNotePage ? 1 : 2,
          }}
        >
          <Grid size={12}>
            <CustomSelect
              required
              label="Medicine Name"
              placeholder="Select Or Search Medicine"
              onSearch={(_, val) => setSearchKey(val)}
              name="medicalCodes"
              handleBlur={formik.handleBlur}
              options={transformedMedicines}
              value={formik.values?.medicalCodes as any}
              onChange={(v) => formik.setFieldValue("medicalCodes", v)}
              error={formik.touched.medicalCodes && formik.errors.medicalCodes}
            />
          </Grid>
          <Grid size={3}>
            <NumberInput
              label="Quantity"
              textFieldProps={{
                placeholder: "Enter Quantity",
                name: "quantity",
                value: formik.values.quantity,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
            />
          </Grid>
          <Grid size={3}>
            <CustomSelect
              label="Dosage"
              placeholder="Dosage Unit"
              options={DOSAGE_LIST}
              value={formik.values?.dosage}
              onChange={(v) => formik.setFieldValue("dosage", v)}
            />
          </Grid>
          <Grid size={3}>
            <CustomSelect
              label="Frequency"
              placeholder="Select Frequency"
              options={FREQUENCY_LIST}
              value={formik.values?.frequency}
              onChange={(v) => formik.setFieldValue("frequency", v)}
            />
          </Grid>

          <Grid size={3}>
            <CustomSelect
              label="When"
              placeholder="When"
              options={WHEN_LIST}
              value={formik.values?.dosageWhen}
              onChange={(v) => formik.setFieldValue("dosageWhen", v)}
            />
          </Grid>
          <Grid size={3}>
            <CustomSelect
              label="Route"
              placeholder="Select Route"
              options={ROUTE_LIST}
              value={formik.values?.direction}
              onChange={(v) => formik.setFieldValue("direction", v)}
            />
          </Grid>
          <Grid size={3}>
            <CustomDatePicker
              label="Start Date"
              placeholder="Choose Date"
              name="medicationStartDate"
              handleBlur={formik.handleBlur}
              required
              value={
                formik.values.medicationStartDate
                  ? new Date(formik.values.medicationStartDate)
                  : undefined
              }
              onChange={(val: any) => {
                formik.setFieldValue("medicationStartDate", val?.toISOString());
                if (
                  formik.values?.medicationLastDate &&
                  val &&
                  new Date(formik.values?.medicationLastDate) < new Date(val)
                ) {
                  formik.setFieldValue(
                    "medicationLastDate",
                    val?.toISOString()
                  );
                }
              }}
              error={
                (formik.touched.medicationStartDate ||
                  formik.submitCount > 0) &&
                formik.errors.medicationStartDate
              }
            />
          </Grid>
          <Grid size={3}>
            <CustomDatePicker
              disabled={formik.values.lifeTime}
              label="End Date"
              minDate={dayjs(
                formik.values?.medicationStartDate
                  ? new Date(formik.values.medicationStartDate)
                  : new Date()
              )}
              placeholder="Choose Date"
              value={
                formik.values.medicationLastDate
                  ? new Date(formik.values.medicationLastDate)
                  : undefined
              }
              onChange={(val) =>
                formik.setFieldValue("medicationLastDate", val?.toISOString())
              }
            />
          </Grid>

          <Grid size={3}>
            <FormControlLabel
              label={<Text>For Lifetime</Text>}
              control={
                <Checkbox
                  name="lifeTime"
                  checked={formik.values.lifeTime}
                  onChange={(_, checked) => {
                    if (checked) {
                      formik.setFieldValue("medicationLastDate", undefined);
                    }
                    formik.setFieldValue("lifeTime", checked);
                  }}
                />
              }
            />
          </Grid>
          <Grid size={6}>
            <AsyncSelect
              // required
              loading={isLoading || isRefetching}
              value={formik.values.prescribedBy}
              name="prescribedBy"
              handleBlur={formik.handleBlur}
              onSearch={(v) => setProviderSearchKey(v)}
              label="Prescribed By"
              placeholder="Select Provider"
              options={transformedProviders}
              onChange={(v) => {
                formik.setFieldValue("prescribedBy", v);
              }}
              // error={formik.touched.prescribedBy && formik.errors.prescribedBy}
            />
          </Grid>
          <Grid size={12}>
            <CustomTextArea
              label="Note"
              rows={3}
              variant="outlined"
              name="note"
              placeholder="Type here"
              onBlur={formik.handleBlur}
              value={formik.values?.note}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      )}
    </StyledFormModal>
  );
};

export default UpsertMedicationsDrawer;
